import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  applyToOpportunity,
  createOpportunity,
  deleteOpportunity,
  deleteOpportunityApplication,
  getOpportunity,
  getOpportunityApplication,
  getOpportunities,
  getOpportunityApplications,
  updateOpportunity,
  updateOpportunityStatus,
} from "./requests";
import type {
  CustomQueryOptions,
  Filter,
  Opportunity,
  Pagination,
} from "../types";

export const useGetOpportunities = (
  filter: Filter & { workspace?: string },
  options?: CustomQueryOptions<{ data: any[]; pagination: Pagination }>
) => {
  return useQuery({
    queryKey: ["opportunities", `all-${JSON.stringify(filter)}`],
    queryFn: () => getOpportunities(filter),
    ...options,
  });
};

export const useGetOpportunity = (
  id: string,
  options?: CustomQueryOptions<any>
) => {
  return useQuery({
    queryKey: ["opportunities", id],
    queryFn: () => getOpportunity(id),
    enabled: !!id,
    ...options,
  });
};

export const useCreateOpportunity = (
  onSuccess: (response: any) => void,
  onError: (error: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Opportunity) => createOpportunity(data),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["opportunities"],
        exact: false,
      });
    },
    retry: false,
  });
};

export const useUpdateOpportunity = (
  onSuccess: (response: any) => void,
  onError: (error: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      updateOpportunity(id, data),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["opportunities"],
        exact: false,
      });
    },
  });
};

export const useDeleteOpportunity = (
  onSuccess: (response: any) => void,
  onError: (error: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteOpportunity(id),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["opportunities"],
        exact: false,
      });
    },
  });
};

export const useUpdateOpportunityStatus = (
  onSuccess: (response: any) => void,
  onError: (error: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      updateOpportunityStatus(id, { status }),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["opportunities"],
        exact: false,
      });
    },
  });
};

export const useApplyToOpportunity = (
  onSuccess: (response: any) => void,
  onError: (error: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: {
        contactPersonName: string;
        contactPersonEmail: string;
        contactPersonPhone: string;
        experienceFile?: string;
        customResponses?: Array<{
          questionId: string;
          questionLabel: string;
          response?: string | number | string[];
          files?: Array<{
            name: string;
            url: string;
          }>;
        }>;
      };
    }) => applyToOpportunity(id, data),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["opportunities"],
        exact: false,
      });
    },
  });
};

export const useGetOpportunityApplications = (
  opportunityId: string,
  filter?: Filter & { workspace?: string },
  options?: CustomQueryOptions<{ data: any[]; pagination: Pagination }>
) => {
  return useQuery({
    queryKey: [
      "opportunities",
      `${opportunityId}-applications-${JSON.stringify(filter || {})}`,
    ],
    queryFn: () => getOpportunityApplications(opportunityId, filter),
    enabled: !!opportunityId,
    ...options,
  });
};

export const useGetOpportunityApplication = (
  opportunityId: string,
  applicationId: string,
  options?: CustomQueryOptions<any, Error, any, [string, string, string, string]>
) => {
  return useQuery({
    queryKey: ["opportunities", opportunityId, "applications", applicationId],
    queryFn: () => getOpportunityApplication(opportunityId, applicationId),
    enabled: !!opportunityId && !!applicationId,
    ...options,
  });
};

export const useDeleteOpportunityApplication = (
  opportunityId: string,
  onSuccess: (response: any) => void,
  onError: (error: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (applicationId: string) =>
      deleteOpportunityApplication(opportunityId, applicationId),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["opportunities", `${opportunityId}-applications`],
        exact: false,
      });
      // Also invalidate the opportunities list to update submission counts
      queryClient.invalidateQueries({
        queryKey: ["opportunities"],
        exact: false,
      });
    },
  });
};
